import { formatTicketNo } from "@seagames/contracts";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { formatDateTime, formatShort } from "../../lib/format/datetime";
import { ChevronRightIcon } from "../transport/icons";
import { PriorityTile, TicketStatusChip } from "./status";
import type { HelpdeskTicketRow } from "./types";

/** "Bukit Jalil Games Village, room A-412" — or just the building, or "—". */
export function useTicketPlace() {
	const { t } = useTranslation("helpdesk");
	return (row: Pick<HelpdeskTicketRow, "building" | "room">): string =>
		row.building ? (row.room ? t("ticket.buildingRoom", { building: row.building.name, room: row.room.room_label }) : row.building.name) : "—";
}

/**
 * One ticket (her `.ereq`): priority tile, title, "HD-000001 · High · 23 Aug,
 * 3:49PM" (absolute, viewer's clock — her relative "6 h ago" is dropped,
 * PLAN A14), the contingent and where, the status chip. The whole row opens
 * the ticket; `note` adds the host's latest note under it (the NOC list).
 */
export function TicketRow({ row, onOpen, showOrg = true, note, icon }: { row: HelpdeskTicketRow; onOpen: () => void; showOrg?: boolean; note?: ReactNode; icon?: ReactNode }) {
	const { t } = useTranslation("helpdesk");
	const place = useTicketPlace();
	const org = row.organization;
	return (
		<button
			type="button"
			onClick={onOpen}
			className="flex w-full min-w-0 cursor-pointer items-center gap-[0.75rem] rounded-box border border-gms-line bg-base-100 px-[0.875rem] py-[0.75rem] text-left transition-colors outline-none hover:border-gms-navy focus-visible:ring-2 focus-visible:ring-gms-navy/40"
		>
			<PriorityTile priority={row.priority} icon={icon} />
			<span className="grid min-w-0 flex-1 justify-items-start gap-[0.25rem]">
				<b className="max-w-full text-[0.875rem] leading-[1.3] font-semibold text-gms-navy-ink sm:truncate">{row.title}</b>
				{/* Phones: the chip goes under the title, so the title keeps the width. */}
				<TicketStatusChip status={row.status} className="sm:hidden" />
				<span className="text-[0.75rem] text-gms-ink-3">
					<span className="tabular-nums">{formatTicketNo(row.ticket_no)}</span>
					<span aria-hidden> · </span>
					{t(`priority.label.${row.priority}`)}
					<span aria-hidden> · </span>
					<time dateTime={row.created_at} title={formatDateTime(row.created_at)}>
						{formatShort(row.created_at)}
					</time>
				</span>
				<span className="inline-flex max-w-full flex-wrap items-center gap-x-[0.3125rem] text-[0.78125rem] text-gms-ink-2">
					{showOrg && org && (
						<>
							<b className="font-semibold text-gms-navy-ink" title={org.name}>
								{org.code ?? org.name}
							</b>
							<span aria-hidden>·</span>
						</>
					)}
					<span className="min-w-0">{place(row)}</span>
				</span>
				{note}
			</span>
			<span className="flex shrink-0 items-center gap-[0.5rem]">
				<TicketStatusChip status={row.status} className="max-sm:hidden" />
				<ChevronRightIcon aria-hidden className="size-[1rem] text-gms-ink-3" />
			</span>
		</button>
	);
}
